import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, User, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      // Store credentials for future requests
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      // Verify credentials
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      
      // Store auth in sessionStorage
      localStorage.setItem("adminAuth", auth);
      
      toast.success("Connexion réussie");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = error.response?.data?.detail || "Identifiants incorrects";
      toast.error(errorMsg, {
        duration: 5000,
        description: "Si le problème persiste, videz le cache ou utilisez la navigation privée."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-24 w-auto mx-auto mb-4"
          />
          <p className="text-slate-500 mt-1">Espace Administration</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-lg border-slate-100">
          <CardHeader className="text-center pb-2">
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Connexion</CardTitle>
            <CardDescription>Entrez vos identifiants pour accéder au tableau de bord</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="username">Identifiant</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="username"
                    name="username"
                    value={credentials.username}
                    onChange={handleChange}
                    placeholder="admin"
                    className="pl-10 input-style"
                    data-testid="admin-username-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={credentials.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="pl-10 input-style"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary mt-6"
                data-testid="admin-login-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Back to site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-slate-500 hover:text-slate-700 text-sm transition-colors"
            data-testid="back-to-site-btn"
          >
            ← Retour au site
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
